<script setup lang="ts">
import { computed } from 'vue'
import { storeToRefs } from 'pinia'
import LibraryContentPanel from '@/components/LibraryContentPanel.vue'
import LibraryRemoveConfirmDialog from '@/components/LibraryRemoveConfirmDialog.vue'
import { useLibraryStore } from '@/library/library.store'
import type { CopyGoalProps, LibraryGoal } from '@/library/library_types'
import ShareGoalPanel from '@/library/ShareGoalPanel.vue'
import type { SavingState } from '@/presentlevels/presentlevels_types'
import { CONTENT_TYPES } from '@/util/access_visibility'
import ProgressMonitoringPanel from '../ProgressMonitoringPanel.vue'
import AnchorPageStandards from '../anchor-page/AnchorPageStandards.vue'
import { useAnchorPageStore } from '../anchor_page.store'

const props = defineProps<{
  savedCopy: LibraryGoal
  saveStatus: SavingState
  copyGoal: CopyGoalProps
}>()

const emit = defineEmits<{
  save: []
  discard: []
  print: []
  removeConfirmed: []
}>()

defineSlots<{
  review?(): unknown
}>()

const anchorPageStore = useAnchorPageStore()
const { hasPMAs, actionPermissions } = storeToRefs(anchorPageStore)
const libraryStore = useLibraryStore()
const { loadFolderError } = storeToRefs(libraryStore)

const isProgressMonitoringShown = computed(() => Boolean(props.savedCopy.title && props.savedCopy.uuid) && !hasPMAs.value)
const removeDialogGroup = computed(() => `${props.savedCopy.uuid}_${libraryStore.currentFolder?.uuid}`)
const standardCopySource = computed(() => ({ title: props.savedCopy.title, uuid: props.savedCopy.uuid }))
</script>

<template>
  <p v-if="loadFolderError" role="alert" class="rounded-sm border border-gray-400 bg-white p-4 text-text-md text-gray-900" data-testid="saved-anchor-folder-error">
    We couldn’t load the folder this goal is saved in, so it can’t be edited right now. Please refresh the page or try again later.
  </p>
  <div v-else class="flex flex-col gap-5">
    <LibraryContentPanel
      :content-uuid="savedCopy.uuid"
      :content-type="CONTENT_TYPES.SAVED_SUPER_PAGE"
      :source-content-uuid="savedCopy.goal_uuid"
      :is-original-page-available="anchorPageStore.isOriginalPageAvailable(savedCopy.goal_uuid)"
      :save-status="saveStatus"
      :updated-at="savedCopy.updated_at"
      :copy-goal="copyGoal"
      @save="emit('save')"
      @discard="emit('discard')"
      @print="emit('print')"
      @remove-confirmed="emit('removeConfirmed')"
    />
    <ShareGoalPanel
      v-if="savedCopy.goal_uuid"
      :uuid="savedCopy.uuid"
      :share-state="Boolean(savedCopy.shared)"
      :share-link-state="savedCopy.share_link ?? ''"
      :share-permissions="actionPermissions.shareSavedGoal"
    />
    <LibraryRemoveConfirmDialog :group="removeDialogGroup" />
  </div>

  <ProgressMonitoringPanel
    v-if="isProgressMonitoringShown"
    class="fs-exclude"
    :content-type="CONTENT_TYPES.SAVED_SUPER_PAGE"
    :content-title="savedCopy.title"
    :content-uuid="savedCopy.uuid"
  />

  <slot name="review" />

  <AnchorPageStandards :standard-copy-source="standardCopySource" />
</template>
